import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getApiUrl } from '@/config/api';
import { ACCOUNT_KEYS, clearAccountStorage } from '@/config/accountStorage';
import { getDeviceSignature } from '@/utils/deviceSignature';
import { captureApiError, addBreadcrumb } from '@/observability/errorReporter';

/**
 * Axios client for the ORGANIZATION account console (internal realm).
 *
 * Separate from `axiosInstance` (candidate/JMS realm) on purpose: the two
 * sessions are issued by different realms and must never share a bearer token.
 * Tokens are read from localStorage rather than Redux so the interceptor always
 * sees the current value, including immediately after a rotation.
 */

const accountAxios = axios.create({
  baseURL: getApiUrl(''),
  headers: { 'Content-Type': 'application/json' },
});

/** No interceptors — used for refresh so a 401 here cannot recurse. */
const accountRawAxios = axios.create({
  baseURL: getApiUrl(''),
  headers: { 'Content-Type': 'application/json' },
});

export const accountTokens = {
  get access(): string | null {
    try {
      return localStorage.getItem(ACCOUNT_KEYS.accessToken);
    } catch {
      return null;
    }
  },
  get refresh(): string | null {
    try {
      return localStorage.getItem(ACCOUNT_KEYS.refreshToken);
    } catch {
      return null;
    }
  },
  set(accessToken: string, refreshToken?: string | null): void {
    try {
      localStorage.setItem(ACCOUNT_KEYS.accessToken, accessToken);
      if (refreshToken) {
        localStorage.setItem(ACCOUNT_KEYS.refreshToken, refreshToken);
      }
    } catch {
      // noop — storage may be unavailable
    }
  },
};

/** Fired when the session cannot be recovered; the shell routes to sign-in. */
type ExpiryListener = () => void;
const expiryListeners = new Set<ExpiryListener>();

export function onAccountSessionExpired(listener: ExpiryListener): () => void {
  expiryListeners.add(listener);
  return () => expiryListeners.delete(listener);
}

function endSession(): void {
  clearAccountStorage();
  expiryListeners.forEach((listener) => listener());
}

accountAxios.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    addBreadcrumb('http', `${(config.method ?? 'get').toUpperCase()} ${config.url ?? ''}`);
    const token = accountTokens.access;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers['X-Device-Signature'] = getDeviceSignature();
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
const queue: Array<{ resolve: (token: string) => void; reject: (reason?: unknown) => void }> = [];

const drainQueue = (error: Error | null, token: string | null) => {
  queue.forEach(({ resolve, reject }) => {
    if (error || !token) reject(error ?? new Error('Session expired'));
    else resolve(token);
  });
  queue.length = 0;
};

interface RefreshEnvelope {
  data: {
    accessToken?: string;
    refreshToken?: string | null;
    // Some realms nest the pair; accept both rather than guessing.
    tokens?: { accessToken: string; refreshToken?: string | null };
  };
}

accountAxios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    captureApiError(error);
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status !== 401 || !original || original._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => queue.push({ resolve, reject }))
        .then((token) => {
          if (original.headers) original.headers.Authorization = `Bearer ${token}`;
          return accountAxios(original);
        });
    }

    original._retry = true;
    isRefreshing = true;

    const refreshToken = accountTokens.refresh;
    if (!refreshToken) {
      isRefreshing = false;
      endSession();
      drainQueue(new Error('No refresh token'), null);
      return Promise.reject(error);
    }

    try {
      const response = await accountRawAxios.post<RefreshEnvelope>('auth/refresh', {
        refreshToken,
        deviceSignature: getDeviceSignature(),
      });

      const payload = response.data?.data;
      const accessToken = payload?.tokens?.accessToken ?? payload?.accessToken;
      const rotated = payload?.tokens?.refreshToken ?? payload?.refreshToken ?? refreshToken;

      if (!accessToken) throw new Error('Refresh returned no access token');

      accountTokens.set(accessToken, rotated);
      isRefreshing = false;
      drainQueue(null, accessToken);

      if (original.headers) original.headers.Authorization = `Bearer ${accessToken}`;
      return accountAxios(original);
    } catch (refreshError) {
      isRefreshing = false;
      const failure = refreshError as AxiosError;
      const isNetworkError =
        !failure.response || failure.code === 'ERR_NETWORK';

      // A network blip must not sign the owner out mid-session; only a real
      // rejection from the server ends it.
      if (!isNetworkError) endSession();
      drainQueue(failure, null);
      return Promise.reject(refreshError);
    }
  }
);

export { accountAxios, accountRawAxios };
